"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Divider,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import Link from "next/link";

// Large, clear color swatches with names
const topColors = [
  { color: "#047857", name: "Emerald Green", why: "Adds rich contrast without overpowering pale skin." },
  { color: "#D4A5A5", name: "Dusty Rose", why: "Soft and flattering—perfect for low-contrast complexions." },
  { color: "#1E3A5F", name: "Navy Blue", why: "A gentler alternative to black that won't wash you out." },
  { color: "#B4A7D6", name: "Lavender", why: "Enhances cool undertones and brightens the face." },
  { color: "#FAF9F6", name: "Soft White", why: "Avoids harsh contrast while keeping things clean." },
  { color: "#36454F", name: "Charcoal Gray", why: "More balanced than black for pale skin tones." },
  { color: "#722F37", name: "Burgundy", why: "Deep, rich, and complements both cool and neutral undertones." },
  { color: "#9CAF88", name: "Sage Green", why: "Muted and natural—great for softer features." },
  { color: "#E8B4B8", name: "Cool Pink", why: "Adds life to pale skin without overwhelming it." },
  { color: "#367588", name: "Teal", why: "Balanced saturation that works across undertones." },
  { color: "#673147", name: "Plum", why: "Deep but not harsh—ideal for evening looks." },
  { color: "#B0C4DE", name: "Powder Blue", why: "Light, airy, and flattering on fair complexions." },
];

const coolUndertoneColors = [
  { color: "#B4A7D6", name: "Lavender" },
  { color: "#E8B4B8", name: "Cool Pink" },
  { color: "#0F52BA", name: "Sapphire Blue" },
  { color: "#047857", name: "Emerald" },
  { color: "#673147", name: "Plum" },
];

const warmUndertoneColors = [
  { color: "#FF7F50", name: "Coral" },
  { color: "#D2B48C", name: "Warm Beige" },
  { color: "#556B2F", name: "Olive Green" },
  { color: "#C19A6B", name: "Muted Mustard" },
  { color: "#CC5A47", name: "Terracotta" },
];

const neutralUndertoneColors = [
  { color: "#367588", name: "Teal" },
  { color: "#D4A5A5", name: "Dusty Rose" },
  { color: "#1E3A5F", name: "Soft Navy" },
  { color: "#C9A9C9", name: "Mauve" },
  { color: "#9CAF88", name: "Sage Green" },
];

const colorsToAvoid = [
  { color: "#000000", name: "Jet Black", why: "Too harsh against light skin", instead: "Charcoal or navy" },
  { color: "#FF1493", name: "Neon Colors", why: "Overpower your features", instead: "Muted jewel tones" },
  { color: "#D4B896", name: "Yellow-Beige", why: "Blends into skin and looks dull", instead: "Soft white or dusty rose" },
  { color: "#FF4500", name: "Bright Orange", why: "Clashes with most undertones", instead: "Terracotta or coral" },
  { color: "#FFFFFF", name: "Pure White", why: "Can look stark and unnatural", instead: "Off-white or cream" },
];

function ColorSwatch({ color, size = 48 }: { color: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: 8,
        border: color === "#FFFFFF" || color === "#FAF9F6" ? "1px solid #e0e0e0" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    />
  );
}

export default function BestColorsForPaleSkinContent() {
  return (
    <Container size="md" py="xl">
      {/* HERO */}
      <Stack gap="xs" mb={48}>
        <Title order={1} size={36} lh={1.2}>
          Best Colors for Pale Skin
        </Title>
        <Text size="xl" c="dimmed" maw={600}>
          Discover the most flattering colors for pale skin—based on undertone, contrast, and real-world outfits.
        </Text>
      </Stack>

      {/* SECTION 1: TOP 12 COLORS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Top 12 Best Colors for Pale Skin
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
          {topColors.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={56} />
              <Box style={{ flex: 1 }}>
                <Text fw={600} size="lg">{item.name}</Text>
                <Text c="dimmed" size="sm">{item.why}</Text>
              </Box>
            </Group>
          ))}
        </SimpleGrid>

        {/* Subtle CTA */}
        <Paper withBorder p="lg" mt={40} radius="md" bg="gray.0">
          <Text size="md" mb="sm">
            Not sure which of these is <em>your</em> best color?
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            rightSection={<IconArrowRight size={16} />}
          >
            Get your exact palette with AI Color Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 2: VISUAL PROOF */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          What These Colors Look Like on Pale Skin
        </Title>

        <Text c="dimmed" mb={32} maw={700}>
          Not all colors look the same on pale skin. The right shades enhance your natural tone—while the wrong ones can make you look washed out or overly harsh.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={32}>
          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="green" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text fw={600}>Flattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#047857" size={40} />
              <ColorSwatch color="#1E3A5F" size={40} />
              <ColorSwatch color="#722F37" size={40} />
              <ColorSwatch color="#B4A7D6" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Rich jewel tones and muted colors create balance with pale skin.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="red" size="sm" radius="xl">
                <IconX size={14} />
              </ThemeIcon>
              <Text fw={600}>Unflattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#000000" size={40} />
              <ColorSwatch color="#FF4500" size={40} />
              <ColorSwatch color="#FF1493" size={40} />
              <ColorSwatch color="#D4B896" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Harsh blacks, neons, and skin-tone beiges wash you out.
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper p="lg" radius="md" bg="blue.0">
          <Text fw={600} mb="xs">Why this works:</Text>
          <Text size="sm">
            Pale skin typically has lower natural contrast. Colors that are too harsh (like jet black or neon tones) can overpower your features, while softer or richer tones create balance.
          </Text>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 3: BY UNDERTONE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Best Colors for Pale Skin by Undertone
        </Title>

        <Stack gap={40}>
          {/* Cool */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Cool Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Pink, red, or bluish skin. Silver jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {coolUndertoneColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Cool tones mirror the natural undertones in your skin, creating harmony and brightness.
            </Text>
          </Box>

          {/* Warm */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Warm Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Peach, yellow, or golden skin. Gold jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {warmUndertoneColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> These colors complement the warmth in your skin instead of clashing with it.
            </Text>
          </Box>

          {/* Neutral */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Neutral Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Mix of both. Both gold and silver jewelry work.</Text>

            <Group gap="lg" mb={16}>
              {neutralUndertoneColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Neutral undertones can handle both warm and cool shades—but muted tones tend to look best.
            </Text>
          </Box>
        </Stack>

        {/* Stronger CTA */}
        <Paper withBorder p="lg" mt={40} radius="md" bg="teal.0">
          <Text fw={600} mb="sm">
            Most people guess their undertone wrong.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            size="md"
            rightSection={<IconArrowRight size={16} />}
          >
            Find your exact undertone + best colors instantly
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 4: COLORS TO AVOID */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Colors That Make Pale Skin Look Washed Out
        </Title>

        <Text c="dimmed" mb={32}>
          Some colors can drain your complexion or create too much contrast.
        </Text>

        <Stack gap={20}>
          {colorsToAvoid.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={48} />
              <Box style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={600}>{item.name}</Text>
                  <Text c="red.6" size="sm">— {item.why}</Text>
                </Group>
                <Text size="sm" c="teal.7">
                  Instead, choose: <strong>{item.instead}</strong>
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 5: SEASONAL COLOR ANALYSIS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          The Secret: Your Exact Color Season Matters
        </Title>

        <Text c="dimmed" mb={8}>
          While general advice helps, the truth is:
        </Text>

        <Paper p="md" radius="md" bg="gray.1" mb={24}>
          <Text fw={600} size="lg" ta="center">
            Not all pale skin is the same.
          </Text>
        </Paper>

        <Text mb={32}>
          Two people with pale skin can look completely different in the same color. That's because your <strong>color season</strong> determines your exact best palette.
        </Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16} mb={32}>
          <Anchor component={Link} href="/color-palette/winter/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#000080" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#DC143C" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFFFFF" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">Winter</Text>
              <Text size="xs" c="dimmed">Bold & high-contrast</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/summer/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#B4A7D6" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#B0C4DE" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#D4A5A5" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Summer</Text>
              <Text size="xs" c="dimmed">Soft & muted cool</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/spring/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#FF7F50" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#40E0D0" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFEFD5" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">Spring</Text>
              <Text size="xs" c="dimmed">Warm & bright</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#556B2F" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#CD853F" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">Autumn</Text>
              <Text size="xs" c="dimmed">Warm & earthy</Text>
            </Paper>
          </Anchor>
        </SimpleGrid>

        {/* Primary CTA */}
        <Paper p="xl" radius="md" bg="teal.6" c="white" ta="center">
          <Text size="lg" fw={600} mb="sm">
            Get your personalized color palette
          </Text>
          <Text size="sm" mb="md" opacity={0.9}>
            Takes 30 seconds. See your exact best colors.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            variant="white"
            size="lg"
            rightSection={<IconArrowRight size={18} />}
          >
            Start Free Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 6: FAQ */}
      <Box mb={48}>
        <Title order={2} size={28} mb={32}>
          Frequently Asked Questions
        </Title>

        <Stack gap={24}>
          <Box>
            <Text fw={600} mb={4}>What colors make pale skin look less washed out?</Text>
            <Text c="dimmed">
              Rich tones like emerald, navy, and burgundy add contrast and bring life to pale skin.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Can pale skin wear black?</Text>
            <Text c="dimmed">
              Yes—but softer alternatives like charcoal or navy are usually more flattering, especially for low-contrast coloring.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What is the best hair color for pale skin?</Text>
            <Text c="dimmed">
              It depends on undertone. Cool undertones suit ash tones, while warm undertones look great in honey and golden shades.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Are pastel colors good for pale skin?</Text>
            <Text c="dimmed">
              Yes—especially muted pastels like lavender, dusty pink, and powder blue. Avoid icy pastels that might wash you out.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Does pale skin have warm or cool undertones?</Text>
            <Text c="dimmed">
              Pale skin can be either! Check your wrist veins: blue/purple = cool, green = warm. Or see which jewelry (silver vs gold) looks better on you.
            </Text>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
